export const getHandoverInventoryItemId = (item = {}) => {
    const candidate = item.handover_item_id
        ?? item.handover_request_item_id
        ?? item.item_id
        ?? item.id;
    const itemId = Number(candidate);

    return Number.isSafeInteger(itemId) && itemId > 0 ? itemId : null;
};

// A line OATRx does not hold stock for is handed over without a question:
// it is always "usable" and never shows the good / damaged fields. Older
// backends do not send the flag; a missing flag means tracked, as before.
export const needsInventoryDecision = (item = {}) => item.inventory_tracked !== false;

const getDecisionForItem = (item, decisions = {}) => {
    const itemId = getHandoverInventoryItemId(item);
    const decision = (itemId ? decisions[itemId] : null)
        || decisions[item.id]
        || {};

    if (!needsInventoryDecision(item) && !decision.condition) {
        return { condition: 'intact' };
    }

    return decision;
};

export const applyDefaultUsableDecisions = (items = [], decisions = {}) => {
    let updatedDecisions = decisions;

    items.forEach((item) => {
        const itemId = getHandoverInventoryItemId(item);
        if (!itemId) return;

        const currentDecision = decisions[itemId] || {};
        if (currentDecision.condition) return;

        if (updatedDecisions === decisions) {
            updatedDecisions = { ...decisions };
        }

        updatedDecisions[itemId] = {
            ...currentDecision,
            condition: 'intact',
        };
    });

    return updatedDecisions;
};

const toQty = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.round(number * 1000) / 1000 : 0;
};

export const hasKnownQty = (item = {}) => Number.isFinite(Number(item.qty)) && Number(item.qty) > 0;

// One dropdown per medication: usable (default), unusable, or some damaged.
// Only "some damaged" asks for a number; the good side is the remainder.
export const setHandoverItemCondition = (item, decision = {}, condition) => {
    const { good_qty: _good, damaged_qty: _damaged, ...rest } = decision;
    if (condition !== 'split') {
        return { ...rest, condition };
    }
    const total = toQty(item.qty);
    const damaged = decision.damaged_qty === undefined || decision.damaged_qty === '' ? '' : toQty(decision.damaged_qty);
    return {
        ...rest,
        condition: 'split',
        damaged_qty: damaged,
        good_qty: damaged === '' ? total : Math.max(0, Math.round((total - damaged) * 1000) / 1000),
    };
};

export const setHandoverDamagedQty = (item, decision = {}, value) => {
    const total = toQty(item.qty);
    const damaged = value === '' ? '' : Math.max(0, toQty(value));
    return {
        ...decision,
        condition: 'split',
        damaged_qty: damaged,
        good_qty: damaged === '' ? total : Math.max(0, Math.round((total - damaged) * 1000) / 1000),
    };
};

export const applyConditionToAllItems = (items = [], decisions = {}, condition) => {
    const updated = { ...decisions };
    items.forEach((item) => {
        const itemId = getHandoverInventoryItemId(item);
        if (!itemId) return;
        updated[itemId] = setHandoverItemCondition(item, decisions[itemId] || {}, condition);
    });
    return updated;
};

export const validateHandoverInventoryDecisions = (items = [], decisions = {}) => {
    const itemIds = items.map(getHandoverInventoryItemId);
    if (itemIds.some((itemId) => itemId === null) || new Set(itemIds).size !== itemIds.length) {
        return 'The returned medication details changed. Refresh the handover request and try again.';
    }

    for (const item of items) {
        const decision = getDecisionForItem(item, decisions);

        if (!decision.condition) {
            return `Select the condition for ${item.drug || `RX ${item.rxnumber || ''}`}.`;
        }

        if (decision.condition === 'split') {
            const name = item.drug || `RX ${item.rxnumber || ''}`;
            if (!hasKnownQty(item)) {
                return `The returned quantity for ${name} is unknown, so choose Usable or Unusable.`;
            }
            const total = toQty(item.qty);
            const damaged = decision.damaged_qty === '' ? NaN : toQty(decision.damaged_qty);
            if (!Number.isFinite(damaged) || damaged <= 0) {
                return `Enter how many of the ${total} ${name} units are damaged.`;
            }
            if (damaged > total) {
                return `Damaged units for ${name} cannot be more than the ${total} returned.`;
            }
        }
    }

    return '';
};

export const buildHandoverInventoryDecisions = (items = [], decisions = {}) => items.map((item) => {
    const itemId = getHandoverInventoryItemId(item);
    const decision = getDecisionForItem(item, decisions);
    const payload = {
        item_id: itemId,
        condition: decision.condition,
    };

    // Only a split sends quantities; usable / unusable stay condition-only,
    // exactly as before, so nothing changes for the common case.
    if (decision.condition === 'split') {
        const total = toQty(item.qty);
        const damaged = toQty(decision.damaged_qty);
        payload.damaged_qty = damaged;
        payload.good_qty = Math.max(0, Math.round((total - damaged) * 1000) / 1000);
    }

    return payload;
});

export const handoverRequestErrorMessage = (requestError, fallback) => {
    const errors = requestError?.response?.data?.data?.error;
    if (errors && typeof errors === 'object') {
        const firstMessage = Object.values(errors)
            .flat()
            .find((message) => typeof message === 'string' && message.trim());
        if (firstMessage) return firstMessage;
    }

    return requestError?.response?.data?.message || fallback;
};
